import { EVENT_AUDIENCE, type EventAudience } from '@wineryxplorer/shared';
import { FormSection } from '../ui/FormSection';
import type { EventFormErrors } from './types';

export interface EventCapacityPricingFieldsProps {
  maxAttendees: string;
  onMaxAttendeesChange: (value: string) => void;
  priceAmount: string;
  onPriceAmountChange: (value: string) => void;
  requiresReservation: boolean;
  onRequiresReservationChange: (value: boolean) => void;
  registrationUrl: string;
  onRegistrationUrlChange: (value: string) => void;
  audience: EventAudience;
  onAudienceChange: (value: EventAudience) => void;
  errors: EventFormErrors;
  touched: Record<string, boolean>;
  onBlur: (field: string) => void;
  isSubmitting: boolean;
}

export const EventCapacityPricingFields = ({
  maxAttendees,
  onMaxAttendeesChange,
  priceAmount,
  onPriceAmountChange,
  requiresReservation,
  onRequiresReservationChange,
  registrationUrl,
  onRegistrationUrlChange,
  audience,
  onAudienceChange,
  errors,
  touched,
  onBlur,
  isSubmitting,
}: EventCapacityPricingFieldsProps) => (
  <FormSection title="Capacity and Pricing">
    {/* Max Attendees */}
    <div>
      <label htmlFor="maxAttendees" className="block text-sm font-medium text-gray-700 mb-1">
        Max Attendees
      </label>
      <input
        id="maxAttendees"
        type="text"
        inputMode="numeric"
        value={maxAttendees}
        onChange={(e) => onMaxAttendeesChange(e.target.value)}
        onBlur={() => onBlur('maxAttendees')}
        disabled={isSubmitting}
        placeholder="Leave empty for unlimited"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.maxAttendees && errors.maxAttendees ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {touched.maxAttendees && errors.maxAttendees && (
        <p className="mt-1 text-sm text-red-600">{errors.maxAttendees}</p>
      )}
    </div>

    {/* Price */}
    <div>
      <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
        Price (USD)
      </label>
      <div className="relative">
        <span className="absolute left-3 top-2 text-gray-500">$</span>
        <input
          id="price"
          type="text"
          inputMode="decimal"
          value={priceAmount}
          onChange={(e) => onPriceAmountChange(e.target.value)}
          onBlur={() => onBlur('price')}
          disabled={isSubmitting}
          placeholder="0.00"
          className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
            touched.price && errors.price ? 'border-red-500' : 'border-gray-300'
          } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
      </div>
      {touched.price && errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
      <p className="mt-1 text-xs text-gray-500">Leave empty for free events</p>
    </div>

    {/* Requires Reservation */}
    <div className="flex items-center">
      <input
        id="requiresReservation"
        type="checkbox"
        checked={requiresReservation}
        onChange={(e) => onRequiresReservationChange(e.target.checked)}
        disabled={isSubmitting}
        className="h-4 w-4 text-primary-700 focus:ring-info-500 border-gray-300 rounded"
      />
      <label htmlFor="requiresReservation" className="ml-2 text-sm text-gray-700">
        Requires reservation/registration
      </label>
    </div>

    {/* Registration / booking link */}
    <div>
      <label htmlFor="registrationUrl" className="block text-sm font-medium text-gray-700 mb-1">
        Booking Link
      </label>
      <input
        id="registrationUrl"
        type="url"
        inputMode="url"
        value={registrationUrl}
        onChange={(e) => onRegistrationUrlChange(e.target.value)}
        onBlur={() => onBlur('registrationUrl')}
        disabled={isSubmitting}
        placeholder="https://www.exploretock.com/..."
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          touched.registrationUrl && errors.registrationUrl ? 'border-red-500' : 'border-gray-300'
        } ${isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      />
      {touched.registrationUrl && errors.registrationUrl && (
        <p className="mt-1 text-sm text-red-600">{errors.registrationUrl}</p>
      )}
      <p className="mt-1 text-xs text-gray-500">
        Tock, Resy or the venue's own page. Leave empty to use the venue's booking link.
      </p>
    </div>

    {/* Audience */}
    <div>
      <label htmlFor="audience" className="block text-sm font-medium text-gray-700 mb-1">
        Audience
      </label>
      <select
        id="audience"
        value={audience}
        onChange={(e) => onAudienceChange(e.target.value as EventAudience)}
        disabled={isSubmitting}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
          isSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
      >
        {EVENT_AUDIENCE.map((aud) => (
          <option key={aud} value={aud}>
            {aud === 'public'
              ? 'Public (open to everyone)'
              : 'Members Only (wine club / allocation list)'}
          </option>
        ))}
      </select>
      <p className="mt-1 text-sm text-gray-500">
        Members-only events are still visible to everyone but labeled as a club perk.
      </p>
    </div>
  </FormSection>
);
